/**
 * Import Words from CSV Dialog Component
 *
 * Dialog for importing words from a CSV file to an existing word list.
 */

import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useImportWordsFromCsv } from '../services/wordListsApi';

interface ImportWordsCsvDialogProps {
  listId: string;
  listName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ImportWordsCsvDialog({
  listId,
  listName,
  onClose,
  onSuccess,
}: ImportWordsCsvDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importWords = useImportWordsFromCsv();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv') && !selectedFile.name.endsWith('.txt')) {
        toast.error('Please select a CSV or TXT file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    try {
      const result = await importWords.mutateAsync({ id: listId, file });

      const messages: string[] = [];
      if (result.added_existing > 0) {
        messages.push(
          `${result.added_existing} existing word${result.added_existing !== 1 ? 's' : ''} added`
        );
      }
      if (result.created_new > 0) {
        messages.push(
          `${result.created_new} new word${result.created_new !== 1 ? 's' : ''} created`
        );
      }
      if (result.skipped_already_in_list > 0) {
        messages.push(`${result.skipped_already_in_list} skipped (already in list)`);
      }
      if (result.failed > 0) {
        messages.push(`${result.failed} failed`);
      }

      toast.success(`Import complete: ${messages.join(', ')}`);

      if (result.failed > 0 && result.failed_words.length > 0) {
        toast.error(`Failed words: ${result.failed_words.join(', ')}`, { duration: 10000 });
      }

      onSuccess();
      onClose();
    } catch (error) {
      toast.error(`Import failed: ${(error as Error).message}`);
    }
  };

  const isImporting = importWords.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Import Words from CSV</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isImporting}
            >
              x
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isImporting ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4" />
              <div className="text-lg font-medium text-gray-900">Importing words...</div>
              <div className="text-sm text-gray-500 mt-2">
                This may take several minutes. For each new word, the system generates images,
                example sentences, translations, and pronunciations.
              </div>
            </div>
          ) : (
            <>
              {/* List info */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">
                  Importing to: <span className="font-medium text-gray-900">{listName}</span>
                </div>
              </div>

              {/* File Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV file *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {file && <p className="mt-2 text-sm text-green-600">Selected: {file.name}</p>}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 rounded-lg p-4 text-sm">
                <div className="font-medium text-blue-900 mb-2">CSV Format:</div>
                <ul className="space-y-1 text-blue-800">
                  <li>• One word per line</li>
                  <li>• No headers needed</li>
                  <li>• Plain text or CSV format</li>
                </ul>
                <div className="mt-3 font-medium text-blue-900">Example:</div>
                <pre className="mt-1 bg-blue-100 rounded p-2 text-blue-900">
                  apple{'\n'}banana{'\n'}orange
                </pre>
              </div>

              {/* What happens */}
              <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm">
                <div className="font-medium text-gray-900 mb-2">What happens:</div>
                <ul className="space-y-1 text-gray-600">
                  <li>• Words already in list are skipped</li>
                  <li>• Existing words in system are added to list</li>
                  <li>• New words: image, translations, pronunciations generated</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            disabled={isImporting}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isImporting || !file}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isImporting ? 'Importing...' : 'Import Words'}
          </button>
        </div>
      </div>
    </div>
  );
}
